import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Disc3, AlertTriangle, Save, Check, Guitar, Link2, X as XIcon, Mic2 } from 'lucide-react';
import { Button, Card, CardContent, FormField, Badge } from '../ui';
import { fetchWithCsrf } from '@utils/fetchWithCsrf';
import { useProductionConfig } from '@/context/ProductionConfigContext';
import { usePersona } from '@/hooks/usePersona';
import { AlbumSessionStatusPanel } from './AlbumSessionStatusPanel';
import type { LiveSession } from './albumSessionStatus';

interface StylePresetSummary { name: string; description: string; isBuiltIn?: boolean }
interface AlbumSessionConfig {
  id?: string;
  enabled: boolean;
  targetCount: number;
  budgetCapUsd: number;
  minDelaySeconds: number;
  maxConsecutiveErrors: number;
  styleMode: 'studio' | 'live';
  albumTitle: string;
  albumArtist: string;
  basePresetName: string | null;
  narrativeBlock: string;
  bpmMin: number;
  bpmMax: number;
  allowedModes: string;   // JSON array string
  allowedMoods: string;
  allowedMeters: string;
  allowedUnits: string;
  baseInstruments: string;
  activeAlbumId?: string | null;
  sessionStartedAt?: string | null;
  sessionCompleted?: number;
  sessionFailed?: number;
  sessionSpentUsd?: number;
  lastRunAt?: string | null;
  lastError?: string | null;
  instrumentForgeConfigJson?: string | null;
  personaSnapshotJson?: string | null;
}

const DEFAULT_CONFIG: AlbumSessionConfig = {
  enabled: false,
  targetCount: 10,
  budgetCapUsd: 3.0,
  minDelaySeconds: 60,
  maxConsecutiveErrors: 3,
  styleMode: 'studio',
  albumTitle: 'Untitled Album',
  albumArtist: 'The Session',
  basePresetName: null,
  narrativeBlock: '',
  bpmMin: 70,
  bpmMax: 140,
  allowedModes: '["dorian","minor","mixolydian","major"]',
  allowedMoods: '["warm","dark","driving"]',
  allowedMeters: '["4/4","6/8","3/4"]',
  allowedUnits: '["1/8","1/16"]',
  baseInstruments: '["Gtr1","Gtr2","Bass","Drums","Hammond"]',
  instrumentForgeConfigJson: null,
  personaSnapshotJson: null,
};

const ALL_MODES = ['dorian', 'minor', 'mixolydian', 'major', 'phrygian', 'lydian', 'locrian'];
const ALL_MOODS = ['warm', 'dark', 'triumphant', 'driving'];
const ALL_METERS = ['4/4', '3/4', '6/8', '2/4', '5/4', '7/8', '12/8'];
const ALL_UNITS = ['1/4', '1/8', '1/16'];

function parseArr(json: string): string[] {
  try { const p = JSON.parse(json); return Array.isArray(p) ? p : []; } catch { return []; }
}

/** What the server has saved and is acting on (not the form: the form can hold edits that were never saved). */
function toLive(c: AlbumSessionConfig): LiveSession {
  return {
    enabled: c.enabled, targetCount: c.targetCount, narrativeBlock: c.narrativeBlock ?? '', budgetCapUsd: c.budgetCapUsd,
    sessionStartedAt: c.sessionStartedAt ?? null, sessionCompleted: c.sessionCompleted ?? 0, sessionFailed: c.sessionFailed ?? 0,
    sessionSpentUsd: c.sessionSpentUsd ?? 0, lastRunAt: c.lastRunAt ?? null, lastError: c.lastError ?? null,
  };
}

const ChipToggle: React.FC<{ options: string[]; value: string; onChange: (next: string) => void }> = ({ options, value, onChange }) => {
  const active = parseArr(value);
  const toggle = (opt: string) => {
    const next = active.includes(opt) ? active.filter(x => x !== opt) : [...active, opt];
    onChange(JSON.stringify(next));
  };
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(opt => {
        const on = active.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`px-2.5 py-1 rounded text-[11px] font-bold uppercase tracking-wider transition-colors ${on ? 'bg-accent text-slate-950' : 'bg-slate-900 border border-slate-700 text-slate-400 hover:border-slate-600'}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
};

export const AlbumSessionsTab: React.FC = () => {
  const { instrumentForgeConfig, instrumentForgeOutput } = useProductionConfig();
  const { activePersona } = usePersona();
  const [config, setConfig] = useState<AlbumSessionConfig>(DEFAULT_CONFIG);
  const [initial, setInitial] = useState<AlbumSessionConfig | null>(null);
  // AUTO-099: the saved session as the server has it, refreshed on every poll and after Save.
  const [live, setLive] = useState<LiveSession | null>(null);
  const serverRunning = useRef(false);
  const [presets, setPresets] = useState<StylePresetSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [cfgRes, presetsRes] = await Promise.all([
          fetchWithCsrf('/api/album-sessions/config'),
          fetchWithCsrf('/api/style-presets/list'),
        ]);
        if (!cfgRes.ok) throw new Error(`HTTP ${cfgRes.status}`);
        const cfgData = await cfgRes.json() as AlbumSessionConfig;
        const presetsData = presetsRes.ok ? await presetsRes.json() : { presets: [] };
        if (cancelled) return;
        setLive(toLive(cfgData));
        serverRunning.current = !!cfgData.enabled;
        if (initial === null) {
          // First load — seed everything from server
          setConfig(cfgData);
          setInitial(cfgData);
        } else {
          // Poll refresh — only sync server-tracked read-only fields, never
          // stomp the user's in-flight edits (toggle, sliders, textareas)
          setConfig(prev => ({
            ...prev,
            id: cfgData.id,
            activeAlbumId: cfgData.activeAlbumId,
            sessionCompleted: cfgData.sessionCompleted,
            sessionFailed: cfgData.sessionFailed,
            sessionSpentUsd: cfgData.sessionSpentUsd,
            lastRunAt: cfgData.lastRunAt,
            lastError: cfgData.lastError,
          }));
        }
        setPresets(Array.isArray(presetsData.presets) ? presetsData.presets : []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Load failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    // Keep polling while the form is switched on OR the server says a session is running (for example one started
    // from another tab).
    const poll = setInterval(() => { if (config.enabled || serverRunning.current) void load(); }, 10_000);
    return () => { cancelled = true; clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.enabled]);

  const dirty = useMemo(() => initial ? JSON.stringify(initial) !== JSON.stringify(config) : true, [initial, config]);

  const linkedInstrumentForge = useMemo(() => {
    if (!config.instrumentForgeConfigJson) return null;
    try {
      const parsed = JSON.parse(config.instrumentForgeConfigJson) as { profileAKey: string | null; profileBKey: string | null; blendRatio: number };
      if (!parsed.profileAKey) return null;
      return parsed.profileBKey
        ? `${parsed.profileAKey} + ${parsed.profileBKey} (${parsed.blendRatio}%)`
        : parsed.profileAKey;
    } catch { return null; }
  }, [config.instrumentForgeConfigJson]);

  const linkedPersona = useMemo(() => {
    if (!config.personaSnapshotJson) return null;
    try {
      const parsed = JSON.parse(config.personaSnapshotJson) as { name: string; useLockedTemplate: boolean };
      return parsed.name || null;
    } catch { return null; }
  }, [config.personaSnapshotJson]);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const res = await fetchWithCsrf('/api/album-sessions/config', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || body.message || `HTTP ${res.status}`);
      }
      const data = await res.json() as AlbumSessionConfig;
      setConfig(prev => ({ ...prev, ...data }));
      setInitial(data);
      setLive(toLive(data));
      serverRunning.current = !!data.enabled;
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally { setSaving(false); }
  };

  const toggleEnabled = () => setConfig({ ...config, enabled: !config.enabled });

  const presetOk = !!config.basePresetName;
  const bpmOk = config.bpmMin <= config.bpmMax;
  const canEnable = presetOk && bpmOk && parseArr(config.allowedModes).length > 0
    && parseArr(config.allowedMoods).length > 0 && parseArr(config.allowedMeters).length > 0
    && parseArr(config.allowedUnits).length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-accent text-slate-950 flex items-center justify-center">
          <Disc3 size={20} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-100">Album Sessions</h2>
          <p className="text-xs text-slate-400">
            Walk-away album generation. Auto-assigned song roles, per-track BPM/key/mode/mood variance, and 2-track continuity context so the album reads like one story.
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 p-4 rounded-xl border border-slate-700 bg-slate-900/40 text-xs text-slate-300">
        <AlertTriangle size={16} className="shrink-0 mt-0.5 text-accent" />
        <div>
          <strong className="text-slate-100">Live.</strong> Toggle ON = the server picks up your config, creates a new Album, and generates one track every {config.minDelaySeconds}s. Each track uses your base preset + a per-track SeedForge roll for musical variance. Prior 2 tracks' summaries are fed as context so the album reads coherent. Songs also save to your Vault tagged <code className="font-mono text-slate-400">album-session,role:X</code>.
        </div>
      </div>

      {/* Toggle + live status */}
      <Card variant="ghost" className={`!border-l-4 !border-l-accent shadow-xl ${config.enabled ? '!bg-accent/5' : '!bg-slate-900'}`}>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-bold text-slate-100">Master Toggle</div>
              <div className="text-xs text-slate-400 mt-1">
                Stops on count or budget cap. Auto-disables on {config.maxConsecutiveErrors} consecutive errors.
              </div>
              {!canEnable && (
                <div className="text-[11px] text-red-400 mt-2">
                  {!presetOk && '• Select a base preset. '}
                  {!bpmOk && '• BPM min ≤ max. '}
                  {parseArr(config.allowedModes).length === 0 && '• Pick at least one mode. '}
                  {parseArr(config.allowedMoods).length === 0 && '• Pick at least one mood. '}
                  {parseArr(config.allowedMeters).length === 0 && '• Pick at least one meter. '}
                  {parseArr(config.allowedUnits).length === 0 && '• Pick at least one unit. '}
                </div>
              )}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={config.enabled}
              onClick={toggleEnabled}
              disabled={loading || !canEnable}
              className={`relative w-14 h-8 rounded-full transition-colors shrink-0 ${config.enabled ? 'bg-accent' : 'bg-slate-700'} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-slate-950 transition-transform ${config.enabled ? 'translate-x-6' : ''}`} />
            </button>
          </div>

          <AlbumSessionStatusPanel live={live} />
        </CardContent>
      </Card>

      {/* Album Identity */}
      <Card variant="ghost" className="!border-l-4 !border-l-accent !bg-slate-900 shadow-xl">
        <CardContent>
          <h3 className="text-sm font-bold text-slate-100 mb-4">Album Identity</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Album Title">
              <input
                type="text"
                value={config.albumTitle}
                onChange={(e) => setConfig({ ...config, albumTitle: e.target.value })}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-accent"
                maxLength={255}
              />
            </FormField>
            <FormField label="Album Artist">
              <input
                type="text"
                value={config.albumArtist}
                onChange={(e) => setConfig({ ...config, albumArtist: e.target.value })}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-accent"
                maxLength={255}
              />
            </FormField>
            <FormField label="Base Style Preset (required)">
              <select
                value={config.basePresetName ?? ''}
                onChange={(e) => setConfig({ ...config, basePresetName: e.target.value || null })}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-accent"
              >
                <option value="">— select preset —</option>
                {presets.map(p => (
                  <option key={p.name} value={p.name}>{p.name}{p.isBuiltIn ? ' (built-in)' : ''}</option>
                ))}
              </select>
            </FormField>
            <FormField
              label="Style"
              description='Studio forbids "live" language in style tags entirely (Suno reads any mention of "live" as a request for concert/crowd atmosphere, even innocuous phrasing like "live room feel"). Live explicitly wants that concert character.'
            >
              <div className="flex bg-slate-950 border border-slate-700 rounded-full p-0.5 w-max">
                {(['studio', 'live'] as const).map(mode => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setConfig({ ...config, styleMode: mode })}
                    className={`px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition-all ${
                      config.styleMode === mode ? 'bg-accent text-slate-950' : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </FormField>
            <FormField label={`Track Count: ${config.targetCount}`}>
              <input
                type="range" min={2} max={20} step={1}
                value={config.targetCount}
                onChange={(e) => setConfig({ ...config, targetCount: parseInt(e.target.value, 10) })}
                className="w-full accent-accent"
              />
              <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                <span>2 (EP)</span>
                <span>10 (LP)</span>
                <span>20 (2LP)</span>
              </div>
            </FormField>
          </div>

          <div className="mt-4">
            <FormField label="Narrative Block (paste from Narrative Engine or write freeform)">
              <textarea
                value={config.narrativeBlock}
                onChange={(e) => setConfig({ ...config, narrativeBlock: e.target.value })}
                rows={6}
                placeholder="Character: a middle-aged mechanic returning to a town that outgrew him. Arc: reckoning. Tone: weary but not defeated. Recurring imagery: rusted equipment, humid summer nights, missed phone calls..."
                className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-xs text-slate-200 font-mono focus:outline-none focus:border-accent resize-y placeholder:text-slate-600"
                maxLength={20000}
              />
            </FormField>
          </div>
        </CardContent>
      </Card>

      {/* InstrumentForge Physics linkage */}
      <Card variant="ghost" className="!border-l-4 !border-l-accent !bg-slate-900 shadow-xl">
        <CardContent>
          <h3 className="text-sm font-bold text-slate-100 mb-2 flex items-center gap-2">
            <Guitar size={16} className="text-accent" /> InstrumentForge Physics
          </h3>
          <p className="text-xs text-slate-400 mb-3">
            Optional. Links your active InstrumentForge guitar/rhythm blend so every track this session generates uses your rig, not just the style preset's genre label.
          </p>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs">
              {linkedInstrumentForge ? (
                <Badge variant="primary-muted" leftIcon={<Link2 size={12} />}>{linkedInstrumentForge}</Badge>
              ) : (
                <span className="text-slate-500 italic">No InstrumentForge blend linked — tracks will use the style preset alone.</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="xs"
                disabled={!instrumentForgeConfig?.profileAKey}
                onClick={() => setConfig({ ...config, instrumentForgeConfigJson: JSON.stringify(instrumentForgeConfig) })}
                leftIcon={<Link2 size={12} />}
              >
                {instrumentForgeOutput ? `Link current: ${instrumentForgeOutput.name}` : 'No active blend to link'}
              </Button>
              {config.instrumentForgeConfigJson && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setConfig({ ...config, instrumentForgeConfigJson: null })}
                  leftIcon={<XIcon size={12} />}
                >
                  Unlink
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Locked Persona linkage */}
      <Card variant="ghost" className="!border-l-4 !border-l-accent !bg-slate-900 shadow-xl">
        <CardContent>
          <h3 className="text-sm font-bold text-slate-100 mb-2 flex items-center gap-2">
            <Mic2 size={16} className="text-accent" /> Locked Persona
          </h3>
          <p className="text-xs text-slate-400 mb-3">
            Optional. Links your active Persona so every track this session generates preserves its locked Style Prompt (BPM/key aside, rolled per track) and vocal/rhythm physics constraints.
          </p>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs">
              {linkedPersona ? (
                <Badge variant="primary-muted" leftIcon={<Link2 size={12} />}>{linkedPersona}</Badge>
              ) : (
                <span className="text-slate-500 italic">No persona linked.</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="xs"
                disabled={!activePersona?.useLockedTemplate}
                onClick={() => setConfig({ ...config, personaSnapshotJson: JSON.stringify(activePersona) })}
                leftIcon={<Link2 size={12} />}
              >
                {activePersona?.useLockedTemplate ? `Link current: ${activePersona.name}` : 'No locked persona active'}
              </Button>
              {config.personaSnapshotJson && (
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setConfig({ ...config, personaSnapshotJson: null })}
                  leftIcon={<XIcon size={12} />}
                >
                  Unlink
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Session Limits */}
      <Card variant="ghost" className="!border-l-4 !border-l-accent !bg-slate-900 shadow-xl">
        <CardContent>
          <h3 className="text-sm font-bold text-slate-100 mb-4">Session Limits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label={`Budget Cap: $${config.budgetCapUsd.toFixed(2)}`}>
              <input type="range" min={0.5} max={15} step={0.25}
                value={config.budgetCapUsd}
                onChange={(e) => setConfig({ ...config, budgetCapUsd: parseFloat(e.target.value) })}
                className="w-full accent-accent" />
              <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                <span>$0.50</span><span>$7.50</span><span>$15</span>
              </div>
            </FormField>
            <FormField label={`Min Delay Between Tracks: ${config.minDelaySeconds}s`}>
              <input type="range" min={30} max={600} step={30}
                value={config.minDelaySeconds}
                onChange={(e) => setConfig({ ...config, minDelaySeconds: parseInt(e.target.value, 10) })}
                className="w-full accent-accent" />
              <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                <span>30s</span><span>5min</span><span>10min</span>
              </div>
            </FormField>
          </div>
        </CardContent>
      </Card>

      {/* SeedForge Variance */}
      <Card variant="ghost" className="!border-l-4 !border-l-accent !bg-slate-900 shadow-xl">
        <CardContent>
          <h3 className="text-sm font-bold text-slate-100 mb-1">Per-Track Musical Variance</h3>
          <p className="text-xs text-slate-400 mb-4">
            Each track rolls fresh values from these pools so no two songs feel identical. Enable/disable options with the chips.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label={`BPM Range: ${config.bpmMin} — ${config.bpmMax}`}>
              <div className="flex items-center gap-2">
                <input type="number" min={40} max={220} value={config.bpmMin}
                  onChange={(e) => setConfig({ ...config, bpmMin: parseInt(e.target.value, 10) || 40 })}
                  className="w-20 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100" />
                <span className="text-slate-500 text-xs">to</span>
                <input type="number" min={40} max={220} value={config.bpmMax}
                  onChange={(e) => setConfig({ ...config, bpmMax: parseInt(e.target.value, 10) || 220 })}
                  className="w-20 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100" />
              </div>
            </FormField>
            <div />

            <FormField label="Allowed Modes">
              <ChipToggle options={ALL_MODES} value={config.allowedModes}
                onChange={(next) => setConfig({ ...config, allowedModes: next })} />
            </FormField>
            <FormField label="Allowed Moods">
              <ChipToggle options={ALL_MOODS} value={config.allowedMoods}
                onChange={(next) => setConfig({ ...config, allowedMoods: next })} />
            </FormField>

            <FormField label="Allowed Meters">
              <ChipToggle options={ALL_METERS} value={config.allowedMeters}
                onChange={(next) => setConfig({ ...config, allowedMeters: next })} />
            </FormField>
            <FormField label="Allowed Note Units">
              <ChipToggle options={ALL_UNITS} value={config.allowedUnits}
                onChange={(next) => setConfig({ ...config, allowedUnits: next })} />
            </FormField>
          </div>

          <div className="mt-4">
            <FormField label="Foregrounded Instruments (JSON array)">
              <input
                type="text"
                value={config.baseInstruments}
                onChange={(e) => setConfig({ ...config, baseInstruments: e.target.value })}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm font-mono text-slate-100 focus:outline-none focus:border-accent"
                placeholder='["Gtr1","Gtr2","Bass","Drums","Hammond"]'
              />
              <p className="text-[10px] text-slate-500 mt-1">Fed to every track as the instrument palette. Free-form JSON — Gtr1/Gtr2/Hammond/Slide/etc.</p>
            </FormField>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] text-slate-500">
          {dirty && <span className="text-amber-400 font-bold">Unsaved changes.</span>}
          {error && <span className="text-red-400"> · {error}</span>}
          {config.activeAlbumId && !config.enabled && (
            <span className="ml-2 text-emerald-400">Album saved. Toggling ON again starts a NEW album.</span>
          )}
        </div>
        <Button
          variant={saved ? 'success' : 'primary'}
          size="sm"
          onClick={save}
          isLoading={saving}
          loadingText="Saving..."
          disabled={loading || !dirty}
          leftIcon={saved ? <Check size={14} /> : <Save size={14} />}
        >
          {saved ? 'Saved' : 'Save Config'}
        </Button>
      </div>
    </div>
  );
};
